/*
 * Flumii — défilement automatique du diaporama superposé.
 * Ce fichier utilise select() du thème. Il ne remplace aucune animation
 * et ne crée ni bouton, ni classe CSS, ni élément visible.
 * Le délai et la section ciblée se règlent dans la balise qui charge ce fichier.
 */
(() => {
  'use strict';

  // Une seule installation, même si la balise est chargée plusieurs fois.
  if (window.__flumiiLayeredAutoplayLoaded) return;
  window.__flumiiLayeredAutoplayLoaded = true;

  const script = document.currentScript;
  const sectionKey = script?.dataset.sectionKey || 'layered_slideshow_UpqTYi';
  const requestedDelay = Number(script?.dataset.delaySeconds ?? 6);
  const delay = Number.isFinite(requestedDelay) && requestedDelay > 0
    ? Math.max(2000, Math.min(60000, requestedDelay * 1000))
    : 6000;
  const tagName = 'layered-slideshow-component';
  const controllers = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const componentReady = window.customElements.whenDefined(tagName);

  // Seul le diaporama demandé est concerné, sur ordinateur comme sur mobile.
  function isTarget(component) {
    const section = component.closest('[id^="shopify-section-"]');
    return section?.id === `shopify-section-${sectionKey}`
      || Boolean(section?.id.endsWith(`__${sectionKey}`));
  }

  function createController(component) {
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const pauses = new Set();
    let timer = null;
    let pointerId = null;
    let destroyed = false;
    let visible = !('IntersectionObserver' in window);

    function tabs() {
      return Array.from(component.refs?.tabs ?? []);
    }

    function keyboardFocusInside() {
      const focused = document.activeElement;
      return Boolean(focused && component.contains(focused)
        && focused.matches(':focus-visible'));
    }

    function clearTimer() {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    }

    function mayPlay() {
      return !destroyed && component.isConnected && visible
        && !document.hidden && !reducedMotion.matches
        && !pauses.size && !keyboardFocusInside()
        && tabs().length > 1;
    }

    function schedule() {
      clearTimer();
      if (!mayPlay()) return;
      timer = window.setTimeout(() => {
        timer = null;
        if (!mayPlay()) return;
        const currentTabs = tabs();
        const active = currentTabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
        const next = active < 0 ? 0 : (active + 1) % currentTabs.length;

        // Seule action sur le diaporama : appeler sa fonction d'origine.
        component.select(next);
        schedule();
      }, delay);
    }

    function pause(reason, state) {
      if (state) pauses.add(reason);
      else pauses.delete(reason);
      schedule();
    }

    component.addEventListener('pointerenter', event => {
      if (event.pointerType === 'mouse') pause('hover', true);
    }, options);
    component.addEventListener('pointerleave', event => {
      if (event.pointerType === 'mouse') pause('hover', false);
    }, options);
    component.addEventListener('pointerdown', event => {
      pointerId = event.pointerId;
      pause('pointer', true);
    }, options);

    function releasePointer(event) {
      if (pointerId === null || event.pointerId !== pointerId) return;
      pointerId = null;
      pause('pointer', false);
    }

    window.addEventListener('pointerup', releasePointer, options);
    window.addEventListener('pointercancel', releasePointer, options);

    component.addEventListener('focusin', schedule, options);
    component.addEventListener('focusout', () => queueMicrotask(schedule), options);
    component.addEventListener('click', schedule, options);
    document.addEventListener('visibilitychange', schedule, options);
    reducedMotion.addEventListener('change', schedule, options);

    // Empêche le défilement pendant la personnalisation du bloc sélectionné.
    function editorSelection(event) {
      const section = component.closest('[id^="shopify-section-"]');
      const targetInside = event.target instanceof Element
        && (section?.contains(event.target) || event.target.contains(component));
      const sameSection = event.detail?.sectionId
        && section?.id === `shopify-section-${event.detail.sectionId}`;
      if (!targetInside && !sameSection) return;
      const kind = event.type.includes(':block:') ? 'editor-block' : 'editor-section';
      pause(kind, event.type.endsWith(':select'));
    }

    for (const eventName of [
      'shopify:section:select', 'shopify:section:deselect',
      'shopify:block:select', 'shopify:block:deselect',
    ]) document.addEventListener(eventName, editorSelection, options);

    let intersectionObserver = null;
    if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(entries => {
        const entry = entries.find(item => item.target === component);
        if (!entry) return;
        visible = entry.isIntersecting && entry.intersectionRatio > 0;
        schedule();
      }, { threshold: [0, 0.1] });
      intersectionObserver.observe(component);
    }

    // Prend aussi en compte un pointeur déjà posé sur la bannière au chargement.
    if (window.matchMedia('(hover: hover)').matches && component.matches(':hover')) {
      pauses.add('hover');
    }

    schedule();
    return {
      refresh: schedule,
      destroy() {
        destroyed = true;
        clearTimer();
        abort.abort();
        intersectionObserver?.disconnect();
      },
    };
  }

  function scan() {
    for (const [component, controller] of controllers) {
      if (!component.isConnected) {
        controller.destroy();
        controllers.delete(component);
      }
    }

    for (const component of document.querySelectorAll(tagName)) {
      if (!isTarget(component) || typeof component.select !== 'function') continue;
      if (!controllers.has(component)) {
        controllers.set(component, createController(component));
      }
    }
  }

  function start() {
    componentReady.then(() => {
      scan();

      document.addEventListener('shopify:section:load', scan);
      document.addEventListener('shopify:section:unload', event => {
        for (const [component, controller] of controllers) {
          if (event.target instanceof Element && event.target.contains(component)) {
            controller.destroy();
            controllers.delete(component);
          }
        }
      });

      // Gère le remplacement de sections sans rechargement complet de la page.
      const observer = new MutationObserver(scan);
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();